// "The tree by the window": a summer wall, bougainvillea and a bicycle → step closer, just out of reach → vines and popping petals → the painting.
export const treeWindow = {
  slug: "the-tree-by-the-window",
  duration: 28,
  stages: [
    { id: "summer", at: 0, hint: "a summer afternoon" },
    { id: "closer", at: 6, hint: "tap to step closer" },
    { id: "vines", at: 13, hint: "the vines" },
    { id: "painting", at: 20.5, hint: "" },
  ],
  line: { text: "Where nature meets the window, the world makes its own art.", from: 14, to: 27.5, pos: "low" },
  touch: { kind: "closer", from: 6, until: 13.5 },
  capLow: true,
  // painted layers: her summer wall (window, bougainvillea, tree, bicycle) in gouache, the vines growing over the frame, a branch swaying in front
  layers: [
    { src: "/arcs/tree/wall-pano.webp", wrap: 2.74, th: 0, h: 8.4, y: 1.8, r: 4.9, show: [-1, 0, 21, 24.5] },
    { src: "/arcs/tree/vines.webp", th: 0.124, y: 2.49, w: 3.3, h: 3.3, r: 4.6, sway: 0.05, show: [13, 18.5, 21.5, 25] },
    { src: "/arcs/tree/branch.webp", th: 0.32, y: 3.9, w: 3.2, h: 2.15, r: 3.4, sway: 0.18, glow: 0.3, show: [-1, 0, 21, 24.5] },
  ],
  ambient: { scene: "summer", until: 22, then: [] },
  look: { start: 0.02, lift: [0, 1], end: 0.02, yaw: [[0, 0.55], [4, -0.3], [7, 0], [28, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "window", pos: "low" },
  speed: [[0, 1], [28, 1]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface, uWarm;
varying vec3 vPos;
${"__NOISE__"}
float hash(float n){ return fract(sin(n * 127.1) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
float seg(vec2 p, vec2 a, vec2 b){ vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }
float ink(float d, float w){ return 1.0 - smoothstep(w * 0.6, w, d); }
// a five-petal blossom (petal shapes, never a round ball)
float flower(vec2 p, float R, float rot){
  float a = atan(p.y, p.x) + rot;
  float r = R * (0.3 + 0.7 * pow(abs(cos(2.5 * a)), 0.7));
  return 1.0 - smoothstep(r * 0.85, r, length(p));
}
vec3 petalCol(float i){
  float k = mod(i, 4.0);
  return k < 0.5 ? vec3(0.84, 0.12, 0.52) : k < 1.5 ? vec3(0.98, 0.58, 0.74) : k < 2.5 ? vec3(0.99, 0.96, 0.93) : vec3(0.86, 0.16, 0.2);
}
vec3 wallScene(vec2 q, float n, float n2){
  vec3 c = mix(vec3(0.93, 0.8, 0.6), vec3(0.99, 0.88, 0.7), n) * (0.9 + 0.1 * n2);
  float dap = smoothstep(0.45, 0.62, snoise(vec3(q * 0.9 + vec2(uT * 0.08, 0.0), uT * 0.05)) * 0.5 + 0.5);
  c *= mix(1.0, 0.8, dap * 0.6);
  vec2 w = q - vec2(0.0, 2.1);
  float inWin = step(abs(w.x), 0.72) * step(abs(w.y), 0.9);
  float frame = step(abs(w.x), 0.84) * step(abs(w.y), 1.02) * (1.0 - inWin);
  vec3 glass = mix(vec3(0.11, 0.18, 0.34), vec3(0.2, 0.29, 0.45), smoothstep(-0.9, 0.9, w.y));
  float strip = (step(abs(w.x + 0.32), 0.06) + step(abs(w.x - 0.3), 0.06)) * step(abs(w.y), 0.82);
  glass = mix(glass, vec3(1.0, 0.84, 0.38), strip * 0.9);
  glass = mix(glass, vec3(0.92, 0.9, 0.84), clamp(ink(abs(w.x), 0.035) + ink(abs(w.y), 0.035), 0.0, 1.0));
  c = mix(c, glass, inWin);
  c = mix(c, vec3(0.9, 0.88, 0.82), frame);
  c *= 1.0 - 0.22 * step(w.y, -1.02) * smoothstep(-1.4, -1.02, w.y) * step(abs(w.x), 0.9);
  // the tree beside it
  float tx = -1.6 + 0.22 * sin(q.y * 0.9);
  c = mix(c, mix(vec3(0.33, 0.22, 0.16), vec3(0.46, 0.33, 0.22), n2), ink(abs(q.x - tx), 0.17 - 0.025 * q.y) * step(q.y, 3.5));
  // bougainvillea spilling magenta around the window
  float zone = 1.0 - smoothstep(0.5, 1.9, length((q - vec2(-0.7, 3.0)) * vec2(0.8, 1.25)));
  zone = max(zone, 0.9 * (1.0 - smoothstep(0.2, 1.0, length((q - vec2(1.05, 2.7)) * vec2(1.6, 0.9)))));
  zone = max(zone, 1.0 - smoothstep(0.3, 1.2, length((q - vec2(-1.7, 3.5)) * vec2(0.9, 1.1))));
  float cl = snoise(vec3(q * 2.6, 1.7)) * 0.5 + 0.5;
  float cl2 = snoise(vec3(q * 7.0, 4.1)) * 0.5 + 0.5;
  float bloom = smoothstep(0.42, 0.5, cl * zone + 0.18 * zone);
  float lv = smoothstep(0.36, 0.43, cl * zone + 0.12 * zone) * (1.0 - bloom);
  c = mix(c, mix(vec3(0.16, 0.32, 0.14), vec3(0.3, 0.48, 0.2), cl2), lv);
  c = mix(c, mix(vec3(0.7, 0.07, 0.42), vec3(0.95, 0.32, 0.66), smoothstep(0.2, 0.8, cl2)), bloom);
  // a bicycle leaning below
  vec2 b = q - vec2(0.95, 0.0);
  float d = abs(length(b - vec2(-0.42, 0.42)) - 0.33);
  d = min(d, abs(length(b - vec2(0.48, 0.42)) - 0.33));
  d = min(d, seg(b, vec2(-0.42, 0.42), vec2(-0.1, 0.95)));
  d = min(d, seg(b, vec2(-0.1, 0.95), vec2(0.0, 0.42)));
  d = min(d, seg(b, vec2(-0.42, 0.42), vec2(0.0, 0.42)));
  d = min(d, seg(b, vec2(-0.1, 0.95), vec2(0.34, 0.95)));
  d = min(d, seg(b, vec2(0.0, 0.42), vec2(0.34, 0.95)));
  d = min(d, seg(b, vec2(0.34, 0.95), vec2(0.48, 0.42)));
  d = min(d, seg(b, vec2(0.28, 1.08), vec2(0.44, 1.08)));
  d = min(d, seg(b, vec2(0.34, 0.95), vec2(0.34, 1.08)));
  d = min(d, seg(b, vec2(-0.2, 1.01), vec2(-0.02, 1.01)));
  c = mix(c, vec3(0.15, 0.3, 0.42), ink(d, 0.032));
  return c;
}
vec3 vines(vec3 c, vec2 q){
  vec2 w = q - vec2(0.0, 2.1);
  float grow = smoothstep(13.0, 19.0, uT);
  float inFrame = step(abs(w.x), 0.9) * step(abs(w.y), 1.1);
  // straight lattice lines across the frame, woven over and under by the curving vines
  float lat = min(abs(w.x - w.y * 0.8), abs(w.x + w.y * 0.8));
  float latK = ink(lat, 0.024) * inFrame * smoothstep(13.5, 15.0, uT);
  float vd = 10.0;
  for (int s = 0; s < 2; s++){
    float sg = float(s) * 2.0 - 1.0;
    float x = sg * 0.84 + 0.1 * sin(w.y * 5.0 + sg);
    float reach = step(w.y + 1.2, grow * 4.6) * step(w.y, 1.1) * step(-1.25, w.y);
    vd = min(vd, mix(10.0, abs(w.x - x), reach));
    float x2 = sg * 0.6 + 0.16 * sin(w.y * 3.2 + 2.0 * sg);
    float reach2 = step(w.y + 1.0, grow * 3.6 - 0.6) * step(abs(w.y), 1.0);
    vd = min(vd, mix(10.0, abs(w.x - x2), reach2));
  }
  float yt = 1.02 + 0.1 * sin(w.x * 6.0);
  float topReach = step(0.84 - abs(w.x), (grow - 0.45) * 1.9) * step(abs(w.x), 0.95);
  vd = min(vd, mix(10.0, abs(w.y - yt), topReach));
  float vine = ink(vd, 0.032);
  float over = step(0.5, fract((w.y + 2.0) * 1.6 + w.x * 0.8));
  vec3 wood = vec3(0.45, 0.32, 0.22);
  c = mix(c, wood, latK * (1.0 - vine * over));
  c = mix(c, vec3(0.2, 0.38, 0.15), vine);
  c = mix(c, wood, latK * vine * (1.0 - over));
  // petals popping open along the vines
  for (int i = 0; i < 16; i++){
    float fi = float(i);
    float side = mod(fi, 3.0) - 1.0;
    vec2 P;
    if (abs(side) < 0.5) { float x = (hash(fi) - 0.5) * 1.6; P = vec2(x, 1.02 + 0.1 * sin(x * 6.0)); }
    else { float y = -1.0 + hash(fi + 2.0) * 2.0; P = vec2(side * 0.84 + 0.1 * sin(y * 5.0 + side), y); }
    float t0 = 14.0 + fi * 0.38;
    float k = smoothstep(t0, t0 + 0.3, uT);
    float pop = k * (1.0 + 0.35 * exp(-max(uT - t0 - 0.3, 0.0) * 5.0));
    float f = flower(w - P, 0.13 * pop, fi * 1.7);
    c = mix(c, petalCol(fi) * (0.85 + 0.15 * smoothstep(0.0, 0.13, length(w - P))), f * step(0.01, k));
  }
  return c;
}
void main(){
  float n = snoise(vPos * 0.8) * 0.5 + 0.5;
  float n2 = snoise(vPos * 3.3 + 2.0) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float melt = smoothstep(20.5, 25.5, uT);
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    vec2 q = vec2(th * uRadius, vPos.y);
    // stepping closer: it draws in and shimmers, then eases back, always just out of reach
    vec2 ctr = vec2(0.0, 2.0);
    q = ctr + (q - ctr) * (1.0 - 0.14 * uWarm);
    q.x += sin(q.y * 16.0 + uT * 5.0) * 0.025 * uWarm + sin(q.y * 4.0 - uT * 1.6) * 0.006;
    col = wallScene(q, n, n2);
    col += vec3(1.0, 0.95, 0.85) * 0.12 * uWarm * (0.5 + 0.5 * sin(q.y * 9.0 + uT * 3.0));
    col = vines(col, q);
    local = vec2(th * uRadius, vPos.y - uCy);
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz;
    col = mix(vec3(0.7, 0.58, 0.44), vec3(0.8, 0.68, 0.52), n) * (0.92 + 0.1 * n2);
    float dap = smoothstep(0.45, 0.62, snoise(vec3(xz * 0.8 + vec2(uT * 0.06, 0.0), uT * 0.05)) * 0.5 + 0.5);
    col *= mix(1.0, 0.8, dap * 0.6);
    vec2 cell = floor(xz / 0.55);
    float h = hash(cell.x * 17.0 + cell.y * 3.1);
    vec2 pc = (cell + 0.5 + 0.3 * vec2(hash(h * 9.0) - 0.5, hash(h * 5.0) - 0.5)) * 0.55;
    col = mix(col, petalCol(floor(h * 8.0)) * 0.9, step(0.82, h) * flower(xz - pc, 0.08, h * 6.0));
    local = xz;
  } else {
    col = mix(vec3(0.62, 0.78, 0.94), vec3(0.5, 0.68, 0.9), n);
    float edge = smoothstep(uRadius * 0.45, uRadius * 0.95, length(vPos.xz));
    float lf = smoothstep(0.42, 0.55, (snoise(vec3(vPos.xz * 1.6, 3.0)) * 0.5 + 0.5) * edge + edge * 0.3);
    col = mix(col, mix(vec3(0.16, 0.32, 0.14), vec3(0.76, 0.12, 0.5), step(0.6, n2)), lf);
    local = vPos.xz;
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  float m = smoothstep(0.0, 0.25, melt * 1.3 - drip * 0.6 - n * 0.25);
  col = mix(col, paint, m);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
